import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

/**
 * Study settings.
 */
export interface Study {
  /**
   * Metadata property name (key) for the study name.
   */
  key?: string;
  /**
   * Study name.
   */
  name: string;

  /**
   * Accepted file types.
   */
  fileTypes?: string[];

  /**
   * Pre-defined study tags.
   */
  tags: StudyTag[];

  /**
   * Specifies if custom tags (metadata) allowed.
   */
  customTags: boolean;
}

/**
 * Study tag types.
 */
export enum TagType {
  /**
   * Free text field.
   */
  Text = 'text',
  /**
   * Question with 'yes' and 'no' answers.
   */
  YesNo = 'yn',
  /**
   * Question with 'yes', 'no' and 'unknown' answers.
   */
  YesNoUnknown = 'ynu'
}

/**
 * Pre-defined study tag.
 */
export interface StudyTag {
  /**
   * Property name (aka metadata key).
   */
  name: string;
  /**
   * Property value (aka metadata value).
   */
  value?: string;
  /**
   * User friendly display name.
   */
  displayName: string;
  /**
   * Tag type.
   */
  type: TagType;
}


/**
 * Service that encapsulates study related operations.
 */
@Injectable({ providedIn: 'root' })
export class StudyService {
  /**
   * Retrieves a list of studies.
   * 
   * @returns Studies list.
   */
  public get(): Observable<Study[]> {
    const result = this.createStudies();
    return of(result);
  }

  /**
   * Creates studies list.
   * 
   * @returns Studies list.
   */
  private createStudies(): Study[] {
    return [
      {
        key: 'studyName',
        name: 'HipCheck',
        fileTypes: [
          'image/jpeg',
          'image/jpg',
          'image/gif',
          'image/png',
          'application/x-zip-compressed',
          'application/zip',
          'multipart/x-zip'
        ],
        tags: [
          {
            name: 'regionId',
            displayName: 'Region',
            type: TagType.Text
          },
          {
            name: 'accountNameId',
            displayName: 'Account Name',
            type: TagType.Text
          },
          {
            name: 'surgeonsNameId',
            displayName: 'Surgeon(s)',
            type: TagType.Text
          },
          {
            name: 'isAsc',
            displayName: 'ASC Account?',
            type: TagType.YesNo
          },
          {
            name: 'datapolicy',
            displayName: 'Account has Data Policy?',
            type: TagType.YesNoUnknown
          }
        ],
        customTags: true
      }
    ];
  }
}
